import { useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { Link as RouterLink, Navigate, Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { LoadingOverlay, PageHeader } from '../../components/ui';
import { graphql } from '../../gql';

const PLANT_DETAIL_QUERY = graphql(`
  query PlantDetail($id: Int!) {
    plantDetail(id: $id) {
      id
      plantName
      plantCode
      managementCode
      area
      country
      region
      businessLine
      operatingCompany
      zipCode
      city
      longitude
      latitude
      windowsTimeZone
      areaFleetManagers
      countryFleetManagers
      pointsOfContact
      assets {
        id
        assetCode
        sapOrderNumber
        serialNumber
        brandName
        modelName
        formOfFinanceName
        status
        isArchived
        readingDate
        smr
        runtime
        fuel
        consumption
      }
    }
  }
`);

const TABS = [
  { value: 'general', label: 'Plant Details' },
  { value: 'assets', label: 'Assets' },
] as const;

export function PlantDetailPage() {
  const { plantId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = Number(plantId);
  const currentTab = location.pathname.split('/').at(-1);
  const isTabRoute = TABS.some((tab) => tab.value === currentTab);

  const { data, loading } = useQuery(PLANT_DETAIL_QUERY, {
    variables: { id },
    skip: !id || isNaN(id),
  });

  useEffect(() => {
    const tabRouteIsActive = TABS.some((tab) => tab.value === location.pathname.split('/').at(-1));
    if (tabRouteIsActive && data?.plantDetail?.plantName) {
      const current = new URLSearchParams(location.search);
      const name = data.plantDetail.plantName;
      if (current.get('name') !== name) {
        navigate({ pathname: location.pathname, search: `?name=${encodeURIComponent(name)}` }, { replace: true });
      }
    }
  }, [data?.plantDetail?.plantName, location.pathname, location.search, navigate]);

  if (!id || isNaN(id)) {
    return <Navigate to="/no-access" replace />;
  }

  if (!loading && data !== undefined && data.plantDetail === null) {
    return <Navigate to="/no-access" replace />;
  }

  const activeTab = isTabRoute ? currentTab : 'general';
  const plant = data?.plantDetail;

  return (
    <main className="max-w-[1440px] mx-auto px-6 py-6">
      <LoadingOverlay visible={loading} />
      <PageHeader title={plant?.plantName ?? 'Plant Details'} secondaryText={plant?.plantCode ?? undefined} />

      {plant != null && (
        <>
          <Tabs value={activeTab} sx={{ borderBottom: 1, borderColor: 'divider' }} data-testid="plant-tabs">
            {TABS.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                component={RouterLink}
                to={{ pathname: tab.value, search: location.search }}
              />
            ))}
          </Tabs>
          <Outlet context={plant} />
        </>
      )}
    </main>
  );
}

export function PlantDetailIndexRedirect() {
  return <Navigate to="general" replace />;
}
